import { useRef, useState } from 'react';
import {
  downloadAttachment,
  useAttachments,
  useDeleteAttachment,
  useUploadAttachment,
} from '../../lib/api/attachments';
import { Attachment, MAX_ATTACHMENT_SIZE_BYTES } from '../../lib/types';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import ConfirmDialog from '../ui/ConfirmDialog';
import { useToast } from '../ui/Toast';

interface Props {
  campaignId?: string;
  brandId?: string;
  /** A brand's Black Friday card for one season (BrandTerms id). */
  bfTermsId?: number;
  title?: string;
  emptyText?: string;
}

const formatBytes = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const fileExt = (name: string): string => {
  const dot = name.lastIndexOf('.');
  if (dot < 0) return '';
  return name.slice(dot + 1).toUpperCase();
};

const fmtDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const AttachmentsSection = ({ campaignId, brandId, bfTermsId, title = 'Attachments', emptyText }: Props) => {
  const filter = campaignId ? { campaign: campaignId } : bfTermsId ? { bf_terms: bfTermsId } : { brand: brandId };
  const { data: items = [], isLoading } = useAttachments(filter);
  const upload = useUploadAttachment();
  const del = useDeleteAttachment();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [confirm, setConfirm] = useState<Attachment | null>(null);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    for (const file of Array.from(files)) {
      if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
        toast.push(`"${file.name}" is too large (max 25 MB)`);
        continue;
      }
      try {
        await upload.mutateAsync({ file, campaign: campaignId, brand: brandId, bf_terms: bfTermsId });
        toast.push(`Uploaded ${file.name}`);
      } catch {
        toast.push(`Could not upload ${file.name}`);
      }
    }
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleDownload = async (att: Attachment) => {
    try {
      await downloadAttachment(att);
    } catch {
      toast.push('Could not download file');
    }
  };

  const handleDelete = async () => {
    if (!confirm) return;
    try {
      await del.mutateAsync(confirm.id);
      toast.push('File deleted');
    } catch {
      toast.push('Could not delete file');
    }
    setConfirm(null);
  };

  return (
    <div className="section">
      <div className="section-head">
        <div className="section-title">{title}</div>
        <Button variant="ghost" onClick={() => inputRef.current?.click()} disabled={upload.isPending}>
          <Icon name="plus" size={13} /> {upload.isPending ? 'Uploading…' : 'Upload file'}
        </Button>
        <input
          ref={inputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>

      <div
        className="card"
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFiles(e.dataTransfer.files);
        }}
        style={{
          borderStyle: dragOver ? 'dashed' : 'solid',
          borderColor: dragOver ? 'var(--sitewide)' : undefined,
          background: dragOver ? 'var(--sitewide-bg)' : undefined,
          transition: 'background 0.12s, border-color 0.12s',
        }}
      >
        {isLoading ? (
          <div style={{ padding: 20, color: 'var(--ink-4)' }}>Loading…</div>
        ) : items.length === 0 ? (
          <div style={{ padding: 20, color: 'var(--ink-4)' }}>
            {emptyText ?? 'No files yet. Drop a PDF or Excel here, or use Upload.'}
          </div>
        ) : (
          items.map((att) => {
            const ext = fileExt(att.name) || '·';
            const uploader =
              att.uploaded_by_details
                ? `${att.uploaded_by_details.first_name || att.uploaded_by_details.email}`.trim()
                : 'someone';
            return (
              <div
                key={att.id}
                className="row"
                style={{
                  gridTemplateColumns: '40px 1fr 90px 180px 28px',
                  cursor: 'default',
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 6,
                    background: 'var(--bg-subtle)',
                    color: 'var(--ink-3)',
                    fontSize: 9.5,
                    fontWeight: 600,
                    display: 'grid',
                    placeItems: 'center',
                    letterSpacing: '0.04em',
                  }}
                >
                  {ext.slice(0, 4)}
                </div>
                <button
                  type="button"
                  onClick={() => handleDownload(att)}
                  style={{
                    background: 'none',
                    border: 'none',
                    textAlign: 'left',
                    padding: 0,
                    cursor: 'pointer',
                    color: 'inherit',
                    minWidth: 0,
                  }}
                >
                  <div
                    className="row-title"
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {att.name}
                  </div>
                  <div className="row-meta">Click to download</div>
                </button>
                <div className="mono" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                  {formatBytes(att.size_bytes)}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--ink-4)' }}>
                  {uploader} · {fmtDate(att.uploaded_at)}
                </div>
                <button
                  type="button"
                  className="icon-btn"
                  onClick={() => setConfirm(att)}
                  title="Delete"
                  style={{ color: 'var(--ink-4)' }}
                >
                  <Icon name="x" size={13} />
                </button>
              </div>
            );
          })
        )}
      </div>

      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={handleDelete}
        title={confirm ? `Delete "${confirm.name}"?` : ''}
        description="This will permanently remove the file. This cannot be undone."
        confirmLabel="Delete file"
        danger
        busy={del.isPending}
      />
    </div>
  );
};

export default AttachmentsSection;
